/**
 * Job-record write gates (RD-ROLE — operations read-only, 2026-07-13).
 *
 * Product decision (founder): the `operations` role is READ-ONLY on JOB
 * RECORDS. Operations can VIEW job history + detail but cannot LOG a job or
 * EDIT a job's line items / notes / date / worker. A parallel migration revokes
 * the server grants (`job_line_items` write RLS + `replace_job_line_items` RPC
 * exclude operations), so these are pure UX gates that keep the client from
 * surfacing a write affordance the server will now reject — the DB is the real
 * boundary (see CLAUDE.md "No client-side enforcement of authorization").
 *
 * Operations KEEPS all dispatch actions (reschedule, no-service, coverage,
 * lifecycle) — those are NOT job-record writes and stay available. These
 * predicates gate ONLY the create-a-job (Log Job) and edit-a-completed-job
 * affordances.
 *
 * Kept as pure, exhaustively-tested predicates rather than inline `role ===
 * 'operations'` checks so the rule lives in one place and every call site reads
 * the same intent. `undefined`/unknown roles fall through to "allowed" — the
 * navigation gate (featureAccess.js) + server RLS already exclude non-eligible
 * roles from reaching these surfaces at all, so failing OPEN here never grants a
 * real write (RLS denies) but avoids stranding a legitimate role on a race where
 * `role` hasn't resolved yet.
 */

/** Can `role` START logging a job (open the Log Job flow)? */
export function canLogJob(role) {
  return role !== 'operations';
}

/** Can `role` EDIT an existing job record (line items / notes / date / worker)? */
export function canEditJobRecord(role) {
  return role !== 'operations';
}

/**
 * Can `role` EDIT the reason of an existing no_service (SKIP) entry?
 *
 * Backed by an RLS denial: `no_service` UPDATE is admin-only (operations was
 * never granted it), so the EditNoServiceModal → direct `no_service` UPDATE
 * fails server-side for ops. This predicate keeps the "Edit" affordance off the
 * operations no-service detail so ops never hits that denial. Ops still MARKS
 * (create), SHIFTS the date (edit_shifted_date RPC), and DELETES a no-service —
 * only the reason rewrite of an existing entry is admin-only. Same fail-open
 * convention (unknown role → true; the server is the real boundary).
 */
export function canEditNoServiceReason(role) {
  return role !== 'operations';
}

/**
 * Can `role` SEE invoice / billing data on the shared admin shell?
 *
 * Operations is the office "money-blind" seat. Operations' invoice SELECT
 * grant was REVOKED at the DB (migration 20260714130000) — ops now hits a
 * hard RLS denial on `invoices`. This predicate is defense-in-depth ON TOP OF
 * that denial: it keeps every invoice affordance off the operations surface,
 * since ops rides the admin shell where invoice data would otherwise bleed
 * onto job surfaces (billing-status glyphs, lock banners, the invoice editor).
 *
 * Same fail-open convention as the others: an unknown / unresolved role
 * returns `true`. Failing open here only ever shows a transient invoice
 * affordance during a role-resolution race — the RLS denial still blocks the
 * actual read — and it avoids stranding admin/billing on a race.
 */
export function canViewInvoices(role) {
  return role !== 'operations';
}

/**
 * RD-ROLE-005 — which FIELD roles may see prices. Mirrors the DB default of
 * company_settings.price_visible_field_roles ('{supervisor}') so a client that
 * has not loaded settings yet behaves exactly like the server.
 */
export const DEFAULT_PRICE_VISIBLE_FIELD_ROLES = Object.freeze(['supervisor']);

/**
 * Can `role` SEE prices (catalog, line prices, job totals) on any surface?
 *
 * admin + billing: always. operations: never (money-blind seat). worker +
 * supervisor: only when the tenant setting lists them. UNLIKE the sibling
 * predicates this one FAILS CLOSED on an unknown/unresolved role — a dollar
 * painted at a blind role is the failure this predicate exists to prevent,
 * and an admin whose role has not resolved only loses the dollar for one
 * render. Server twin: public.current_user_can_view_prices(tenant_id).
 */
export function canViewPrices(role, priceVisibleFieldRoles = DEFAULT_PRICE_VISIBLE_FIELD_ROLES) {
  if (role === 'admin' || role === 'billing') return true;
  if (role === 'worker' || role === 'supervisor') {
    return Array.isArray(priceVisibleFieldRoles) && priceVisibleFieldRoles.includes(role);
  }
  return false;
}
